export enum ApprovalStatus {
    Pending = 1,
    Approved = 2,
    Rejected = 3,
}

// Org membership/subscription state (#321, API contract from #304). Only
// meaningful when approvalStatus is Approved. Admins always get Active.
export enum MembershipStatus {
    None = 0,
    Active = 1,
    PaymentRequired = 2,
    Suspended = 3,
    Cancelled = 4,
    // The org has no active approved location (its only one was soft-deleted).
    // approvalStatus stays Approved — this is NOT an admin suspension and NOT a
    // reverted/pending application.
    NoActiveLocation = 5,
    // Approved but has never had a payment method, so nothing has ever been
    // charged (DecisionLog §1.6: approval no longer charges — capturing a card
    // is what activates the membership). This is the state the add-card panel
    // serves, and it is NOT a lapse: nothing has failed.
    AwaitingPaymentMethod = 6,
    // Approved and paid-up, but an organization member seated on NONE of the
    // org's locations (#410): every ship-to read fails closed for them, so the
    // store is near-empty. NOT a wall state — the gate stays open; the copy
    // that explains it is #417.
    NoLocationMembership = 7,
}

// The statuses that close the customer wall (#321 / #375). An allow-list, not
// "anything but Active": NoLocationMembership (7) is open by contract, None
// cannot occur for an Approved user, and a value this build has not learned
// yet must fail OPEN — walling a paying customer behind lapsed-payment copy
// for an unknown integer is the worse failure. Absent / null (an older API, a
// future nullable DTO) fail open the same way.
export const isMembershipWalled = (
    status: MembershipStatus | null | undefined,
): boolean =>
    status === MembershipStatus.PaymentRequired ||
    status === MembershipStatus.Suspended ||
    status === MembershipStatus.Cancelled ||
    status === MembershipStatus.NoActiveLocation ||
    status === MembershipStatus.AwaitingPaymentMethod;

// Mirrors the API's RoleType (ints on the wire, per the project's enum
// convention). Only Admin is meaningful to the UI today (#401).
export enum RoleType {
    Admin = 1,
}

export interface UserResponse {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    // PLATFORM roles only (User.Permissions as RoleType ints, #401) — always
    // sent, `[]` until the caller has a User row. Organization / location
    // roles are not reported here.
    permissions: RoleType[];
    // Not carried on the CIAM claims, so it is absent until the caller has a
    // User row with one (#237).
    phoneNumber?: string;
    approvalStatus: ApprovalStatus;
    // Present only when approvalStatus is Rejected.
    rejectionReason?: string;
    // Optional until the API side (#304) deploys — undefined is treated as
    // Active so a newer UI against an older API doesn't wall everyone.
    membershipStatus?: MembershipStatus;
    // #445 (audit C-11): the orders whose BANK payment was returned, listed only
    // while the organization is Suspended and only for returns since it last
    // returned to good standing. They turn the suspended wall into the
    // "Payment Returned" page. Never carries the bank's return code. Optional
    // like membershipStatus: absent (an API before PR 379) reads as none.
    returnedPaymentOrders?: ReturnedPaymentOrder[];
    // #445: every listed return's organization has a card saved after that
    // return. It stops the page asking for a card; it never lifts the hold (an
    // admin does). Absent reads as false.
    hasCardSinceReturn?: boolean;
}

// One entry of UserResponse.returnedPaymentOrders (#445).
export interface ReturnedPaymentOrder {
    id: string;
    orderNumber: number;
    orderNumberFormatted: string;
    total: number;
}
